import React, { useEffect, useRef, useCallback } from 'react'
import lottie, { AnimationItem } from 'lottie-web'

const Footer: React.FC = () => {
  const footerRef = useRef<HTMLElement>(null)
  const animContainerRef = useRef<HTMLDivElement>(null)
  const animInstanceRef = useRef<AnimationItem | null>(null)
  const hasPlayedRef = useRef(false)

  const checkVisibility = useCallback(() => {
    if (!animInstanceRef.current || !footerRef.current) return
    const rect = footerRef.current.getBoundingClientRect()
    const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
    const ratio = visibleHeight / rect.height

    if (ratio > 0.3 && !hasPlayedRef.current) {
      animInstanceRef.current.goToAndPlay(0)
      hasPlayedRef.current = true
    } else if (ratio < 0.1 && hasPlayedRef.current) {
      hasPlayedRef.current = false
      animInstanceRef.current.goToAndStop(0)
    }
  }, [])

  useEffect(() => {
    if (!animContainerRef.current) return

    fetch('/Assets/CyranoQuillMotionFooter.json')
      .then(res => res.json())
      .then(data => {
        if (!animContainerRef.current) return
        animInstanceRef.current = lottie.loadAnimation({
          container: animContainerRef.current,
          renderer: 'svg',
          loop: false,
          autoplay: false,
          animationData: data,
          rendererSettings: { preserveAspectRatio: 'xMidYMid meet' },
        })
        checkVisibility()
      })
      .catch(() => { /* animation failed to load — footer still renders fine */ })

    window.addEventListener('scroll', checkVisibility, { passive: true })
    return () => {
      window.removeEventListener('scroll', checkVisibility)
      animInstanceRef.current?.destroy()
    }
  }, [checkVisibility])

  return (
    <footer
      ref={footerRef}
      style={{
        background: '#eae8e4',
        borderTop: '2px solid rgba(212, 173, 187, 0.3)',
        padding: '4rem 0 2rem 0',
        position: 'relative',
        overflow: 'hidden',
        minHeight: 200,
        marginTop: '2rem',
      }}
    >
      {/* Container */}
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '0 2rem',
        }}
      >
        {/* Content: links + animation area */}
        <div className="footer-content-react" style={{ display: 'flex', alignItems: 'flex-start', gap: '2rem' }}>
          {/* Links grid */}
          <div className="footer-links-react" style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '2rem' }}>
            {/* About */}
            <div>
              <h3 style={sectionHeadingStyle}>About</h3>
              <ul style={ulStyle}>
                <li style={liStyle}>
                  <a href="/landing.html#features" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                    Features
                  </a>
                </li>
                <li style={liStyle}>
                  <a href="/landing.html#how-it-works" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                    How it Works
                  </a>
                </li>
                <li style={liStyle}>
                  <a href="/landing.html#pricing" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                    Pricing
                  </a>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h3 style={sectionHeadingStyle}>Resources</h3>
              <ul style={ulStyle}>
                <li style={liStyle}>
                  <a href="/sign-in" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                    Log In
                  </a>
                </li>
                <li style={liStyle}>
                  <a href="#" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                    Grief support
                  </a>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 style={sectionHeadingStyle}>Company</h3>
              <ul style={ulStyle}>
                <li style={liStyle}>
                  <a href="#" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                    About
                  </a>
                </li>
                <li style={liStyle}>
                  <a href="mailto:support@cyrano.cloud" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                    Contact
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Quill Lottie animation — matches landing page footer */}
          <div
            className="footer-animation-react"
            style={{
              position: 'absolute',
              top: '50%',
              right: 20,
              width: 500,
              height: 400,
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            <div
              ref={animContainerRef}
              style={{ width: '100%', height: '100%', maxWidth: 500, maxHeight: 400 }}
            />
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div
        style={{
          borderTop: '1px solid rgba(25, 40, 92, 0.1)',
          marginTop: '2rem',
          paddingTop: '2rem',
          textAlign: 'center',
          color: '#8c83b4',
          fontSize: '0.85rem',
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <p style={{ margin: 0 }}>&copy; 2026 Cyrano. All rights reserved.</p>
      </div>

      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 768px) {
          .footer-content-react {
            flex-direction: column !important;
            gap: 1.5rem !important;
          }
          .footer-links-react {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 1.5rem !important;
          }
          .footer-animation-react {
            display: none !important;
          }
        }
        @media (max-width: 480px) {
          .footer-links-react {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }
        }
      `}</style>
    </footer>
  )
}

// Styles matching landing page footer CSS
const sectionHeadingStyle: React.CSSProperties = {
  fontFamily: "'Playfair Display', serif",
  fontSize: '1.1rem',
  fontWeight: 600,
  marginBottom: '1rem',
  color: '#131e40',
  marginTop: 0,
}

const ulStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
}

const liStyle: React.CSSProperties = {
  marginBottom: '0.5rem',
}

const linkStyle: React.CSSProperties = {
  color: '#8c83b4',
  textDecoration: 'none',
  fontSize: '0.9rem',
  transition: 'color 0.2s ease',
  fontFamily: "'Inter', sans-serif",
}

const hoverIn = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.currentTarget.style.color = '#19285c'
}

const hoverOut = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.currentTarget.style.color = '#8c83b4'
}

export default Footer
